import type {
  Condition,
  ObservationOperand,
} from '#src/observation-logic/conditionSchema.ts'
import {
  type ConditionEnv,
  evaluateCondition,
  type Tri,
  triAnd,
} from '#src/observation-logic/evaluate.ts'
import {
  isEmptyValue,
  type ObservationValue,
} from '#src/observation-logic/value.ts'

// An observation in the note, in document order.
export interface ShowWhenNode {
  id: string
  code: string
  system?: string
  // A name authors use for it, matched like its code.
  name?: string
  // The innermost section it sits in.
  sectionId?: string
  showWhen?: Condition
  value: ObservationValue | null
  // Its value is still being read (or the read failed): not known yet, so
  // conditions reading it are undecided rather than unanswered.
  pending?: true
}

export interface ShowWhenSection {
  id: string
  parentId?: string
  showWhen?: Condition
  // How many nodes come before it in the document, for `nearest`. Without
  // it, a section sits where its first node does.
  index?: number
}

export interface ShowWhenResult {
  // Whether each node and section shows, its sections' conditions included.
  nodes: Map<string, Tri>
  sections: Map<string, Tri>
  // `node:<id>` and `section:<id>` whose condition never settles, or that
  // reads its own content. Their condition evaluates to `unknown`.
  cycles: string[]
}

interface Unit {
  key: string
  condition: Condition
  // Where the unit sits, for `nearest`.
  index: number
  selfNodeId?: string
  sectionId?: string
}

// Every show-when condition in the note, evaluated together. A node hidden
// by its own condition or a section's counts as unanswered wherever it is
// read, so chains (A shows B shows C) settle by repeating to a fixpoint;
// conditions still changing after that are cycles and fail open to
// `unknown`.
export function evaluateShowWhen(
  nodes: readonly ShowWhenNode[],
  sections: readonly ShowWhenSection[],
  variable: ConditionEnv['variable']
): ShowWhenResult {
  const sectionById = new Map(sections.map((section) => [section.id, section]))
  const chains = new Map<string, string[]>()
  const chain = (sectionId: string | undefined): string[] => {
    if (!sectionId) {
      return []
    }
    const known = chains.get(sectionId)
    if (known) {
      return known
    }
    const seen: string[] = []
    let current: string | undefined = sectionId
    while (current && !seen.includes(current)) {
      seen.push(current)
      current = sectionById.get(current)?.parentId
    }
    chains.set(sectionId, seen)
    return seen
  }
  const inSection = (node: ShowWhenNode, sectionId: string): boolean =>
    chain(node.sectionId).includes(sectionId)
  // A section sits where its first node does.
  const sectionIndex = (sectionId: string): number => {
    const first = nodes.findIndex((node) => inSection(node, sectionId))
    return first === -1 ? nodes.length : first
  }

  const own = new Map<string, Tri>()
  const units: Unit[] = []
  nodes.forEach((node, index) => {
    own.set(`node:${node.id}`, true)
    if (node.showWhen) {
      units.push({
        key: `node:${node.id}`,
        condition: node.showWhen,
        index,
        selfNodeId: node.id,
      })
    }
  })
  for (const section of sections) {
    own.set(`section:${section.id}`, true)
    if (section.showWhen) {
      units.push({
        key: `section:${section.id}`,
        condition: section.showWhen,
        index: section.index ?? sectionIndex(section.id),
        sectionId: section.id,
      })
    }
  }

  const sectionShown = (sectionId: string | undefined): Tri =>
    triAnd(chain(sectionId).map((id) => own.get(`section:${id}`) ?? true))
  const nodeShown = (node: ShowWhenNode): Tri =>
    triAnd([own.get(`node:${node.id}`) ?? true, sectionShown(node.sectionId)])

  const matches = (node: ShowWhenNode, operand: ObservationOperand): boolean =>
    (node.code === operand.code &&
      (!operand.system || !node.system || node.system === operand.system)) ||
    (!operand.system && node.name === operand.code)

  const currentValue = (
    node: ShowWhenNode
  ): ObservationValue | null | 'missing' => {
    const shown = nodeShown(node)
    if (shown === 'unknown' || (shown && node.pending)) {
      return 'missing'
    }
    return shown ? node.value : null
  }

  // The nodes an operand could read. A section's condition reads the note
  // without its own content: showing it is what would answer that.
  const candidatesFor = (operand: ObservationOperand, unit: Unit) =>
    nodes.flatMap((node, index) =>
      node.id !== unit.selfNodeId &&
      matches(node, operand) &&
      !(unit.sectionId && inSection(node, unit.sectionId))
        ? [{ node, index }]
        : []
    )

  const resolve = (
    operand: ObservationOperand,
    unit: Unit
  ): ShowWhenNode | undefined => {
    const candidates = candidatesFor(operand, unit)
    if (candidates.length === 0) {
      return undefined
    }
    if (operand.scope === 'any') {
      const answered = candidates.filter(({ node }) => {
        const value = currentValue(node)
        return value !== 'missing' && !isEmptyValue(value)
      })
      return (answered.at(-1) ?? candidates.at(-1))?.node
    }
    const before = candidates.filter(({ index }) => index < unit.index)
    return (before.at(-1) ?? candidates[0])?.node
  }

  // A section whose condition reads a node found only inside itself can
  // never be decided: showing it is what would answer the condition.
  const selfReading = new Set<string>()
  for (const unit of units) {
    if (unit.sectionId) {
      const sectionId = unit.sectionId
      for (const operand of observationOperands(unit.condition)) {
        if (
          candidatesFor(operand, unit).length === 0 &&
          nodes.some(
            (node) => matches(node, operand) && inSection(node, sectionId)
          )
        ) {
          selfReading.add(unit.key)
        }
      }
    }
  }

  const evaluate = (unit: Unit): Tri => {
    if (selfReading.has(unit.key)) {
      return 'unknown'
    }
    return evaluateCondition(unit.condition, {
      variable,
      observation: (operand) => {
        const target = resolve(operand, unit)
        return target ? currentValue(target) : 'missing'
      },
    })
  }

  const settle = (): Set<string> => {
    let changed = new Set<string>()
    for (let pass = 0; pass <= units.length + 1; pass++) {
      changed = new Set()
      for (const unit of units) {
        const next = evaluate(unit)
        if (next !== own.get(unit.key)) {
          own.set(unit.key, next)
          changed.add(unit.key)
        }
      }
      if (changed.size === 0) {
        break
      }
    }
    return changed
  }
  // Conditions still flipping are fixed at `unknown`, then the rest settle
  // again around them.
  const cyclic = settle()
  if (cyclic.size > 0) {
    for (const key of cyclic) {
      selfReading.add(key)
    }
    settle()
  }

  return {
    nodes: new Map(nodes.map((node) => [node.id, nodeShown(node)])),
    sections: new Map(
      sections.map((section) => [section.id, sectionShown(section.id)])
    ),
    cycles: [...selfReading],
  }
}

// Every observation the condition reads.
function observationOperands(condition: Condition): ObservationOperand[] {
  switch (condition.type) {
    case 'all':
    case 'any':
      return condition.conditions.flatMap(observationOperands)
    case 'not':
      return observationOperands(condition.condition)
    case 'answered':
    case 'compare':
      return condition.operand.source === 'observation'
        ? [condition.operand]
        : []
  }
}
